import type { Award, TradePrediction } from "@etimad-cascade/shared";

export type AwardDateSort = "awardDateDesc" | "awardDateAsc";

export interface AwardFilters {
  query: string;
  trade: string;
  region: string;
  type: string;
  confidence: number;
}

export const DEFAULT_AWARD_FILTERS: AwardFilters = {
  query: "",
  trade: "",
  region: "",
  type: "",
  confidence: 0,
};

export interface AwardPage {
  rows: Award[];
  pages: number;
  page: number;
}

export interface AwardDetailsSelection {
  award: Award;
  predictions: TradePrediction[];
}

export interface ContractorProfile {
  name: string;
  awards: Award[];
  totalAwards: number;
  totalAwardValueSar: number | null;
  averageAwardValueSar: number | null;
  firstAwardDate: string | null;
  latestAwardDate: string | null;
  authorities: string[];
  regions: string[];
  projectTypes: string[];
  demandSummary: OpportunityCategorySummary[];
  history: HistoricalActivity[];
}

export interface AuthorityProfile {
  name: string;
  awards: Award[];
  totalAwards: number;
  totalAwardValueSar: number | null;
  averageAwardValueSar: number | null;
  firstAwardDate: string | null;
  latestAwardDate: string | null;
  activeContractors: number;
  regions: string[];
  projectTypes: string[];
  contractors: { name: string; awardCount: number; totalAwardValueSar: number | null }[];
  demandSummary: OpportunityCategorySummary[];
  history: HistoricalActivity[];
}

export interface TradeProfile {
  tradeCode: string;
  tradeName: string;
  awards: Award[];
  totalAwards: number;
  averageConfidence: number | null;
  authorities: string[];
  contractors: string[];
  regions: string[];
  projectTypes: string[];
  history: HistoricalActivity[];
}

export interface HistoricalActivity {
  label: string;
  awardCount: number;
}

export type OpportunityLevel = "High" | "Medium" | "Low";

export interface OpportunityCategorySummary {
  tradeCode: string;
  tradeName: string;
  awardCount: number;
  confidenceScore: number;
  opportunityLevel: OpportunityLevel;
}

export interface ExecutiveDashboard {
  totalAwards: number;
  totalAwardValueSar: number | null;
  activeAuthorities: number;
  activeContractors: number;
  regionsCovered: number;
  highConfidenceOpportunityCategories: number;
  awardActivity: { label: string; awardCount: number }[];
  topAuthorities: { name: string; awardCount: number; totalAwardValueSar: number | null }[];
  topContractors: { name: string; awardCount: number; totalAwardValueSar: number | null }[];
  opportunityCategories: OpportunityCategorySummary[];
}

const opportunityLevel = (prediction: TradePrediction): OpportunityLevel =>
  prediction.demandIntensity === "HIGH"
    ? "High"
    : prediction.demandIntensity === "MEDIUM"
      ? "Medium"
      : "Low";

function buildOpportunitySummary(predictions: TradePrediction[]): OpportunityCategorySummary[] {
  const rank: Record<OpportunityLevel, number> = { Low: 1, Medium: 2, High: 3 };
  const rows = new Map<
    string,
    {
      tradeName: string;
      awardIds: Set<string>;
      confidenceScore: number;
      opportunityLevel: OpportunityLevel;
    }
  >();

  for (const prediction of predictions) {
    const level = opportunityLevel(prediction);
    const row = rows.get(prediction.tradeCode) ?? {
      tradeName: prediction.tradeName,
      awardIds: new Set<string>(),
      confidenceScore: 0,
      opportunityLevel: level,
    };
    row.awardIds.add(prediction.awardId);
    row.confidenceScore = Math.max(row.confidenceScore, prediction.confidenceScore);
    if (rank[level] > rank[row.opportunityLevel]) row.opportunityLevel = level;
    rows.set(prediction.tradeCode, row);
  }

  return [...rows.entries()]
    .map(([tradeCode, row]) => ({
      tradeCode,
      tradeName: row.tradeName,
      awardCount: row.awardIds.size,
      confidenceScore: row.confidenceScore,
      opportunityLevel: row.opportunityLevel,
    }))
    .sort((a, b) => b.awardCount - a.awardCount || a.tradeName.localeCompare(b.tradeName));
}

const uniqueStrings = (values: (string | null)[]): string[] =>
  [...new Set(values.filter((value): value is string => Boolean(value)))].sort((a, b) =>
    a.localeCompare(b),
  );

function buildHistory(awards: Award[]): HistoricalActivity[] {
  const activity = new Map<string, number>();
  for (const award of awards) {
    if (!award.awardDate) continue;
    const label = award.awardDate.slice(0, 7);
    activity.set(label, (activity.get(label) ?? 0) + 1);
  }
  return [...activity.entries()]
    .map(([label, awardCount]) => ({ label, awardCount }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function sortAwardsByAwardDate(awards: Award[], sort: AwardDateSort): Award[] {
  return [...awards].sort((a, b) => {
    if (a.awardDate === null && b.awardDate === null) return 0;
    if (a.awardDate === null) return 1;
    if (b.awardDate === null) return -1;
    const comparison = a.awardDate.localeCompare(b.awardDate);
    return sort === "awardDateAsc" ? comparison : -comparison;
  });
}

export function filterAwards(
  awards: Award[],
  predictions: TradePrediction[],
  filters: AwardFilters,
): Award[] {
  const query = filters.query.toLocaleLowerCase();
  const predictionsByAward = new Map<string, TradePrediction[]>();
  for (const prediction of predictions) {
    const rows = predictionsByAward.get(prediction.awardId) ?? [];
    rows.push(prediction);
    predictionsByAward.set(prediction.awardId, rows);
  }

  return awards.filter(award => {
    const relevantPredictions = predictionsByAward.get(award.id) ?? [];
    const matchesQuery =
      !query ||
      [award.titleAr, award.titleEn, award.authorityName, award.contractorName].some(value =>
        (value ?? "").toLocaleLowerCase().includes(query),
      );

    return (
      matchesQuery &&
      (!filters.trade || relevantPredictions.some(prediction => prediction.tradeCode === filters.trade)) &&
      (!filters.region || award.region === filters.region) &&
      (!filters.type || award.projectType === filters.type) &&
      (!filters.confidence ||
        relevantPredictions.some(prediction => prediction.confidenceScore >= filters.confidence))
    );
  });
}

export function selectAwardDetails(
  awards: Award[],
  predictions: TradePrediction[],
  awardId: string,
): AwardDetailsSelection | null {
  const award = awards.find(row => row.id === awardId);
  if (!award) return null;
  return {
    award,
    predictions: predictions.filter(prediction => prediction.awardId === awardId),
  };
}

export function selectContractorProfile(
  awards: Award[],
  predictions: TradePrediction[],
  contractorName: string,
): ContractorProfile | null {
  const name = contractorName.trim();
  if (!name) return null;
  const contractorAwards = awards.filter(award => award.contractorName === name);
  if (contractorAwards.length === 0) return null;
  const knownValues = contractorAwards
    .map(award => award.awardValueSar)
    .filter((value): value is number => value !== null);
  const dates = contractorAwards
    .map(award => award.awardDate)
    .filter((value): value is string => value !== null)
    .sort((a, b) => a.localeCompare(b));
  const awardIds = new Set(contractorAwards.map(award => award.id));
  const relevantPredictions = predictions.filter(prediction => awardIds.has(prediction.awardId));
  const totalAwardValueSar =
    knownValues.length > 0 ? knownValues.reduce((total, value) => total + value, 0) : null;
  return {
    name,
    awards: sortAwardsByAwardDate(contractorAwards, "awardDateDesc"),
    totalAwards: contractorAwards.length,
    totalAwardValueSar,
    averageAwardValueSar:
      totalAwardValueSar === null ? null : Math.round(totalAwardValueSar / knownValues.length),
    firstAwardDate: dates[0] ?? null,
    latestAwardDate: dates.at(-1) ?? null,
    authorities: uniqueStrings(contractorAwards.map(award => award.authorityName)),
    regions: uniqueStrings(contractorAwards.map(award => award.region)),
    projectTypes: uniqueStrings(contractorAwards.map(award => award.projectType)),
    demandSummary: buildOpportunitySummary(relevantPredictions),
    history: buildHistory(contractorAwards),
  };
}

export function selectAuthorityProfile(
  awards: Award[],
  predictions: TradePrediction[],
  authorityName: string,
): AuthorityProfile | null {
  const name = authorityName.trim();
  if (!name) return null;
  const authorityAwards = awards.filter(award => award.authorityName === name);
  if (authorityAwards.length === 0) return null;
  const knownValues = authorityAwards
    .map(award => award.awardValueSar)
    .filter((value): value is number => value !== null);
  const dates = authorityAwards
    .map(award => award.awardDate)
    .filter((value): value is string => value !== null)
    .sort((a, b) => a.localeCompare(b));
  const awardIds = new Set(authorityAwards.map(award => award.id));
  const relevantPredictions = predictions.filter(prediction => awardIds.has(prediction.awardId));
  const contractorAwards = new Map<string, Award[]>();
  for (const award of authorityAwards) {
    if (!award.contractorName) continue;
    const rows = contractorAwards.get(award.contractorName) ?? [];
    rows.push(award);
    contractorAwards.set(award.contractorName, rows);
  }
  const totalAwardValueSar =
    knownValues.length > 0 ? knownValues.reduce((total, value) => total + value, 0) : null;
  return {
    name,
    awards: sortAwardsByAwardDate(authorityAwards, "awardDateDesc"),
    totalAwards: authorityAwards.length,
    totalAwardValueSar,
    averageAwardValueSar:
      totalAwardValueSar === null ? null : Math.round(totalAwardValueSar / knownValues.length),
    firstAwardDate: dates[0] ?? null,
    latestAwardDate: dates.at(-1) ?? null,
    activeContractors: contractorAwards.size,
    regions: uniqueStrings(authorityAwards.map(award => award.region)),
    projectTypes: uniqueStrings(authorityAwards.map(award => award.projectType)),
    contractors: [...contractorAwards.entries()]
      .map(([contractor, contractorRows]) => {
        const contractorValues = contractorRows
          .map(award => award.awardValueSar)
          .filter((value): value is number => value !== null);
        return {
          name: contractor,
          awardCount: contractorRows.length,
          totalAwardValueSar:
            contractorValues.length > 0
              ? contractorValues.reduce((total, value) => total + value, 0)
              : null,
        };
      })
      .sort((a, b) => b.awardCount - a.awardCount || a.name.localeCompare(b.name)),
    demandSummary: buildOpportunitySummary(relevantPredictions),
    history: buildHistory(authorityAwards),
  };
}

export function selectTradeProfile(
  awards: Award[],
  predictions: TradePrediction[],
  tradeCode: string,
): TradeProfile | null {
  const code = tradeCode.trim();
  if (!code) return null;
  const loadedAwardIds = new Set(awards.map(award => award.id));
  const matchingPredictions = predictions.filter(
    prediction => prediction.tradeCode === code && loadedAwardIds.has(prediction.awardId),
  );
  if (matchingPredictions.length === 0) return null;
  const awardIds = new Set(matchingPredictions.map(prediction => prediction.awardId));
  const tradeAwards = awards.filter(award => awardIds.has(award.id));
  if (tradeAwards.length === 0) return null;

  return {
    tradeCode: code,
    tradeName: matchingPredictions[0]?.tradeName ?? code,
    awards: sortAwardsByAwardDate(tradeAwards, "awardDateDesc"),
    totalAwards: tradeAwards.length,
    averageConfidence: Math.round(
      matchingPredictions.reduce((total, prediction) => total + prediction.confidenceScore, 0) /
        matchingPredictions.length,
    ),
    authorities: uniqueStrings(tradeAwards.map(award => award.authorityName)),
    contractors: uniqueStrings(tradeAwards.map(award => award.contractorName)),
    regions: uniqueStrings(tradeAwards.map(award => award.region)),
    projectTypes: uniqueStrings(tradeAwards.map(award => award.projectType)),
    history: buildHistory(tradeAwards),
  };
}

export function buildExecutiveDashboard(
  awards: Award[],
  predictions: TradePrediction[],
): ExecutiveDashboard {
  const knownValues = awards
    .map(award => award.awardValueSar)
    .filter((value): value is number => value !== null);
  const totalAwardValueSar =
    knownValues.length > 0 ? knownValues.reduce((total, value) => total + value, 0) : null;
  const unique = (values: (string | null)[]): string[] => [
    ...new Set(values.filter((value): value is string => Boolean(value))),
  ];
  const groupAwards = (values: (award: Award) => string | null): Map<string, Award[]> => {
    const groups = new Map<string, Award[]>();
    for (const award of awards) {
      const name = values(award);
      if (!name) continue;
      const rows = groups.get(name) ?? [];
      rows.push(award);
      groups.set(name, rows);
    }
    return groups;
  };
  const summarizeGroups = (groups: Map<string, Award[]>): ExecutiveDashboard["topAuthorities"] =>
    [...groups.entries()]
      .map(([name, rows]) => {
        const rowValues = rows
          .map(award => award.awardValueSar)
          .filter((value): value is number => value !== null);
        return {
          name,
          awardCount: rows.length,
          totalAwardValueSar:
            rowValues.length > 0 ? rowValues.reduce((total, value) => total + value, 0) : null,
        };
      })
      .sort((a, b) => b.awardCount - a.awardCount || a.name.localeCompare(b.name))
      .slice(0, 5);
  const activity = new Map<string, number>();
  for (const award of awards) {
    if (!award.awardDate) continue;
    const label = award.awardDate.slice(0, 7);
    activity.set(label, (activity.get(label) ?? 0) + 1);
  }
  const awardIds = new Set(awards.map(award => award.id));
  const highConfidence = new Set<string>();
  const relevantPredictions: TradePrediction[] = [];
  for (const prediction of predictions) {
    if (!awardIds.has(prediction.awardId)) continue;
    relevantPredictions.push(prediction);
    if (prediction.confidenceScore >= 85) highConfidence.add(prediction.tradeName);
  }
  return {
    totalAwards: awards.length,
    totalAwardValueSar,
    activeAuthorities: unique(awards.map(award => award.authorityName)).length,
    activeContractors: unique(awards.map(award => award.contractorName)).length,
    regionsCovered: unique(awards.map(award => award.region)).length,
    highConfidenceOpportunityCategories: highConfidence.size,
    awardActivity: [...activity.entries()]
      .map(([label, awardCount]) => ({ label, awardCount }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    topAuthorities: summarizeGroups(groupAwards(award => award.authorityName)),
    topContractors: summarizeGroups(groupAwards(award => award.contractorName)),
    opportunityCategories: buildOpportunitySummary(relevantPredictions).slice(0, 5),
  };
}

export function pageAwards(awards: Award[], page: number, pageSize: number): AwardPage {
  const pages = Math.max(1, Math.ceil(awards.length / pageSize));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * pageSize;
  return { rows: awards.slice(start, start + pageSize), pages, page: currentPage };
}
